// =============================================================================
// VOICE REGISTRY — the single indirection point for "which voice for this
// role, right now." Every consumer (agent-session, backchannel, dna.routes'
// TTS/voice-select paths, future mock-interview roles) reads THIS table via
// ONE resolver (apps/api/lib/resolveVoice.ts) — nothing else ever imports
// ElevenLabs directly or knows/cares what the actual sound is.
//
// role -> { provider, providerVoiceId, name, persona, locale, gender, active }
//
// Renaming the agent, swapping providers, or adding a tenth mock-interviewer
// voice becomes a DATA change to this table, never a code change — per
// voice-architecture-law-jason-picks.md ("It's a variable dynamic static
// state until it changes.") Voices are Jason's call, ALONE, always: no row
// here is ever derived or defaulted by the system.
//
// P0 SCOPE ONLY (docs/design/voice-registry/VOICE-REGISTRY-PACKET.md §6,
// docs/design/voice-registry/voice-registry-plan-2026-07-07.md §5): this file
// is `voice_registry` alone. `voice_registry_user_selection` (the per-user
// pick table, FK'd to this one) is explicitly P2 — gated on a gated future
// decision about whether voice_preferences migrates or coexists — and is
// NOT created here.
//
// Mirrors docs/design/voice-registry/voice-registry-plan-2026-07-07.md §5's
// SQL exactly (the SQL file the orchestrator applies via CREATE TABLE IF NOT
// EXISTS carries the same column set). ADDITIVE ONLY (rule #11).
// =============================================================================

import { sql } from 'drizzle-orm';
import { boolean, index, jsonb, pgTable, text, varchar } from 'drizzle-orm/pg-core';
import { id, timestamps } from './_shared';

export const voiceRegistry = pgTable(
  'voice_registry',
  {
    id: id(),
    // Stable key naming WHY the voice is used, not who's using it or what it
    // sounds like: 'companion' | 'mock_interviewer_us_m' | 'locale_interview_au_f' | ...
    // Only 'companion' is live/seeded as of P0 — the rest are named-not-built.
    role: varchar('role', { length: 64 }).notNull(),
    // CHECK constraint applied in the SQL file (Drizzle has no first-class
    // CHECK helper here) — 'elevenlabs' | 'inhouse'.
    provider: varchar('provider', { length: 16 }).notNull(),
    // Opaque, provider-specific. Never parsed/pattern-matched downstream —
    // a leaf value handed straight to the adapter.
    providerVoiceId: varchar('provider_voice_id', { length: 64 }).notNull(),
    // The persona name for this role (e.g. "Mira"/"Meira" for 'companion').
    // Deliberately decoupled from providerVoiceId — renaming the agent is a
    // data edit here with zero relationship to which EL voice id is bound.
    name: varchar('name', { length: 120 }),
    // Free text / small enum describing tone for the PROMPT layer
    // ('warm-friend' | 'challenging-supportive' | ...) — never consumed by
    // the audio layer itself.
    persona: varchar('persona', { length: 32 }),
    // EnglishVariant-shaped ('en-US'/'en-AU'/'en-GB') or a finer accent tag
    // — voice locale sometimes needs more resolution than the 3-bucket text
    // locale model (plan §3.3); this column allows that without forcing it.
    locale: varchar('locale', { length: 16 }),
    // 'm' | 'f' | null (CHECK in the SQL file) — only meaningful for roles
    // explicitly selectable by gender (the mock interviewer). Unused for
    // 'companion' (Jason picks the voice directly, no gender-lookup).
    gender: varchar('gender', { length: 1 }),
    // Superseded rows flip false, NEVER deleted — no-deletions law.
    active: boolean('active').notNull().default(true),
    // Account-drift stamp, mirrors voicePreferences.voiceFingerprint.
    voiceFingerprint: text('voice_fingerprint'),
    // Room to grow without a migration.
    metadata: jsonb('metadata'),
    ...timestamps,
  },
  (t) => ({ roleActiveIdx: index('voice_registry_role_active_idx').on(t.role, t.active) }),
);

/** The shape `resolveVoice()` hands back — full object or nothing, never
 *  partial, mirroring ElevenLabsService.tts()'s own "never a partial" contract. */
export interface ResolvedVoice {
  provider: 'elevenlabs' | 'inhouse';
  providerVoiceId: string;
  name: string | null;
  persona: string | null;
  locale: string | null;
  gender: 'm' | 'f' | null;
}
